import React, { useEffect, useState } from 'react';
import { getEmployeeEarningsOverview, EmployeeEarnings } from '../services/employeeEarningsService';
import { User, DollarSign, Calendar, Users, Award, TrendingUp, Sparkles, Receipt, Wallet } from 'lucide-react';
import { getSettings } from '../services/settingsService';
import { formatCurrency } from '../lib/formatters';

interface Props {
  tenantId: string;
  startDate: string;
  endDate: string;
  sourceFilter: string;
}

export const EmployeeFinancialOverview: React.FC<Props> = ({ tenantId, startDate, endDate, sourceFilter }) => {
  const [earnings, setEarnings] = useState<EmployeeEarnings[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEarnings = async () => {
      setLoading(true);
      try {
        const data = await getEmployeeEarningsOverview(tenantId, startDate, endDate, sourceFilter);
        setEarnings(data);
      } catch (err) {
        console.error('Error fetching employee earnings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEarnings();
  }, [tenantId, startDate, endDate, sourceFilter]);

  // Helper styles for user initials and dynamic gradients
  const getAvatarStyle = (name: string) => {
    const code = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const gradients = [
      'from-blue-500 to-indigo-600 text-white shadow-blue-100',
      'from-emerald-500 to-teal-600 text-white shadow-emerald-100',
      'from-violet-500 to-purple-600 text-white shadow-violet-100',
      'from-rose-500 to-pink-600 text-white shadow-rose-100',
      'from-amber-500 to-orange-600 text-white shadow-amber-100'
    ];
    return gradients[code % gradients.length];
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Calculate aggregates
  const totalTeamEarnings = earnings.reduce((sum, e) => sum + (e.totalEarnings || 0), 0);
  const totalPosEarnings = earnings.reduce((sum, e) => sum + (e.posEarnings || 0), 0);
  const totalCashEarnings = earnings.reduce((sum, e) => sum + (e.cashEarnings || 0), 0);
  const activeStaffCount = earnings.filter(e => e.totalEarnings > 0).length;
  const maxEmployeeEarnings = Math.max(...earnings.map(e => e.totalEarnings), 1);

  return (
    <div id="employee-financial-overview-container" className="p-3.5 sm:p-6 bg-white border border-gray-100 shadow-sm rounded-2xl space-y-5 sm:space-y-6">
      
      {/* Header section with responsive layout */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between border-b border-gray-50 pb-5">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl shadow-inner flex-shrink-0">
            <DollarSign size={20} className="stroke-[2.5]" />
          </div>
          <div>
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight">Employee Financial Overview</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-0.5 whitespace-normal line-clamp-2">Aggregated tip earnings by platform source</p>
          </div>
        </div>

      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="animate-pulse bg-gray-50/70 h-24 rounded-2xl" />
          <div className="animate-pulse bg-gray-50/70 h-64 rounded-2xl" />
        </div>
      ) : (
        <div className="space-y-5">
          
          {/* Quick Aggregate Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="p-3.5 sm:p-4 bg-emerald-50/50 border border-emerald-100/50 rounded-2xl flex flex-col justify-between min-h-[90px]">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-extrabold text-emerald-800 uppercase tracking-widest leading-none">Total Distributed</span>
                <Award size={14} className="text-emerald-600 flex-shrink-0" />
              </div>
              <p className="text-lg sm:text-xl font-black text-emerald-950 truncate" title={formatCurrency(totalTeamEarnings)}>
                {formatCurrency(totalTeamEarnings)}
              </p>
            </div>

            <div className="p-3.5 sm:p-4 bg-blue-50/50 border border-blue-100/50 rounded-2xl flex flex-col justify-between min-h-[90px]">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-extrabold text-blue-800 uppercase tracking-widest leading-none">Active Staff</span>
                <Users size={14} className="text-blue-600 flex-shrink-0" />
              </div>
              <p className="text-lg sm:text-xl font-black text-blue-950">{activeStaffCount} Earners</p>
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-1 p-3.5 sm:p-4 bg-gray-50/60 border border-gray-100/40 rounded-2xl flex flex-col justify-between min-h-[90px]">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-extrabold text-gray-600 uppercase tracking-widest leading-none">POS / Cash Split</span>
                <TrendingUp size={14} className="text-gray-500 flex-shrink-0" />
              </div>
              <div className="space-y-1 mt-0.5">
                <div className="flex justify-between text-[10px] font-black text-gray-700">
                  <span>POS: {formatCurrency(totalPosEarnings)}</span>
                  <span>Cash: {formatCurrency(totalCashEarnings)}</span>
                </div>
                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-sky-500 h-full transition-all duration-500" 
                    style={{ width: `${totalTeamEarnings > 0 ? (totalPosEarnings / totalTeamEarnings) * 100 : 50}%` }} 
                  />
                  <div 
                    className="bg-amber-400 h-full flex-1" 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* List of Earners */}
          {earnings.length === 0 ? (
            <div className="text-center py-12 px-4 bg-gray-50/40 rounded-2xl border border-dashed border-gray-100">
              <Sparkles className="mx-auto text-gray-300 w-8 h-8 mb-2 stroke-[1.5]" />
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">No earnings allocated</p>
              <p className="text-[10px] text-gray-400 mt-1 max-w-xs mx-auto">Try selecting another date range or verify shift approvals on the timeline dashboards.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <h4 className="text-[9px] font-black text-gray-400 uppercase tracking-widest pl-1">Earned Share List</h4>
              
              <div className="space-y-2.5 max-h-[480px] overflow-y-auto pr-1">
                {earnings.map((emp) => {
                  const relativeSharePercentage = Math.round((emp.totalEarnings / maxEmployeeEarnings) * 100);
                  
                  return (
                    <div 
                      key={emp.staffId} 
                      className="p-3.5 border border-gray-50 bg-gray-50/20 hover:bg-gray-50/60 rounded-2xl transition-all duration-200"
                    >
                      {/* Name & Total */}
                      <div className="flex items-center justify-between gap-3 mb-2.5">
                        <div className="flex items-center gap-3">
                          <div className={`w-8.5 h-8.5 rounded-xl bg-gradient-to-br ${getAvatarStyle(emp.staffName)} flex items-center justify-center text-xs font-black shadow-sm`}>
                            {getInitials(emp.staffName)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-extrabold text-gray-900">{emp.staffName}</span>
                              {emp.role && (
                                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[9px] font-bold">
                                  {emp.role}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              {emp.posId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                                  POS ID: {emp.posId}
                                </span>
                              )}
                              {emp.payrollId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9px] font-mono font-bold uppercase">
                                  Payroll ID: {emp.payrollId}
                                </span>
                              )}
                              {emp.staffId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Full Staff ID: ${emp.staffId}`}>
                                  Staff ID: {emp.staffId.slice(0, 8)}
                                </span>
                              )}
                              {emp.email && (
                                <span className="text-[10px] text-gray-400 font-medium">
                                  {emp.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-black text-emerald-600 block">{formatCurrency(emp.totalEarnings)}</span>
                          <span className="text-[9px] text-emerald-800 font-extrabold bg-emerald-50 px-1.5 py-0.5 rounded-md">Total Shared</span>
                        </div>
                      </div>

                      {/* Visual Earning Distribution and Progress bar */}
                      <div className="space-y-2">
                        {/* Progress Bar showing relative share */}
                        <div className="w-full bg-gray-100/60 h-1 rounded-full overflow-hidden">
                          <div 
                            className="bg-blue-500 h-full rounded-full transition-all duration-500" 
                            style={{ width: `${relativeSharePercentage}%` }}
                          />
                        </div>

                        {/* Breakdown block - Responsive Layout */}
                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-gray-100/40">
                          <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                            <Receipt size={11} className="text-sky-500" />
                            <span className="font-extrabold">POS:</span> 
                            <span className="font-black text-gray-900">{formatCurrency(emp.posEarnings)}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 justify-end md:justify-start">
                            <Wallet size={11} className="text-amber-500" />
                            <span className="font-extrabold">Cash/Jar:</span> 
                            <span className="font-black text-gray-900">{formatCurrency(emp.cashEarnings)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
